/**
 * Fetch event handlers.
 *
 * @since 1.0.0
 */

import { popupManager } from '@/frontend/components';
import { transformStoreApiItem } from '@/frontend/transformers/product-transformer';
import type { RedFoxCartData } from '@/frontend/types/cart';
import type { InterceptedData } from '@/frontend/utils/fetch-interceptor/types';

/**
 * Handle cart add item events from fetch interceptor.
 *
 * @since 1.0.0
 *
 * @param data Intercepted data from fetch.
 *
 * @return void
 */
export const handleCartAddItem = (data: InterceptedData): void => {
  // Show cart popup when items are added to cart.
  if (data.response?.items && data.response.items.length > 0) {
    // Find the added item using the request ID.
    const productId = data.request?.id;
    const addedItem = productId
      ? data.response.items.find((cartItem) => cartItem.id === productId)
      : data.response.items[0]; // Fallback to first item.

    if (addedItem) {
      // Transform WooCommerce Store API response to our cart format.
      const transformedProduct = transformStoreApiItem(addedItem);

      // Transform all cart items for full cart display.
      const allCartProducts = data.response.items.map(transformStoreApiItem);

      // Create cart data with proper structure for both display modes.
      const transformedCartData: RedFoxCartData = {
        message: 'Product added to cart',
        products: allCartProducts, // All products in cart.
        just_added_products: [transformedProduct], // Only the added product.
        total_quantity: allCartProducts.reduce((sum, item) => sum + item.quantity, 0),
        total_price: allCartProducts.reduce(
          (sum, item) => sum + item.sale_price * item.quantity,
          0
        ),
        formatted_total_price: transformedProduct.formatted_price,
        show_quantity: true,
        cart_info: {
          count: allCartProducts.reduce((sum, item) => sum + item.quantity, 0),
          cart_url: '/cart',
          checkout_url: '/checkout',
        },
        timestamp: Date.now(),
      };

      // Show the same designed cart popup.
      popupManager.showCart(transformedProduct, transformedCartData, 'wc-cart-added');
    }
  }
};
